import type { Permission } from "@xop/dashboard-shared";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Forbidden } from "../components/Forbidden.js";
import { SessionUnavailable } from "../components/SessionUnavailable.js";
import { PageLoader } from "../components/ui/page-loader.js";
import { useAuth } from "./AuthProvider.js";
import { useCan, useCanAny } from "./permissions.js";

/**
 * Route guard — bounces unauthenticated callers to /login (preserving
 * the destination), and renders a friendly "Forbidden" page when the
 * user is logged in but lacks the required permission.
 */
export function RequireAuth({
  children,
  permission,
  anyOf,
  allowUnenrolled,
  allowMustChange,
}: {
  children: ReactNode;
  permission?: Permission;
  /** Allow the route when the user holds ANY ONE of these permissions.
   * Used where a surface is reachable by more than one tier — e.g. the
   * documentation create/edit pages accept both `documentation:create`
   * (author own) and `documentation:edit` (edit any). Mutually exclusive
   * with `permission`; if both are set, `permission` wins. */
  anyOf?: readonly Permission[];
  /** Set ONLY on the /setup-2fa route so an unenrolled user can reach it
   * without a redirect loop. Everywhere else, an account with no second
   * factor is bounced to /setup-2fa — the client mirror of the server
   * gate in plugins/auth.ts (requireAuth). */
  allowUnenrolled?: boolean;
  /** Set ONLY on the /set-password route. Everywhere else, an account that
   * signed in with a one-time invite password is bounced to /set-password
   * FIRST (before /setup-2fa) — mirrors the server `must_change` gate. */
  allowMustChange?: boolean;
}): JSX.Element {
  const { user, status, refreshSession } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <PageLoader fullScreen />;
  }

  // We have tokens but couldn't reach the server to hydrate the session.
  // Do NOT send the user to /login — the session is fine, the network
  // isn't. See docs/session-lifetime.md.
  if (status === "offline" && !user) {
    return <SessionUnavailable onRetry={() => void refreshSession()} />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Onboarding order: set your own password FIRST, then enroll 2FA. A fresh
  // invitee carries BOTH flags. /set-password allows both (so it renders);
  // /setup-2fa allows only unenrolled (so a must_change user there is sent
  // back to /set-password first).
  if (user.must_change_password && !allowMustChange) {
    return <Navigate to="/set-password" replace />;
  }

  if (user.must_enroll_two_factor && !allowUnenrolled) {
    return <Navigate to="/setup-2fa" replace />;
  }

  if (permission) {
    return <PermissionGate permission={permission}>{children}</PermissionGate>;
  }
  if (anyOf && anyOf.length > 0) {
    return <AnyPermissionGate anyOf={anyOf}>{children}</AnyPermissionGate>;
  }
  return <>{children}</>;
}

function PermissionGate({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}): JSX.Element {
  const allowed = useCan(permission);
  if (!allowed) return <Forbidden requiredPermission={permission} />;
  return <>{children}</>;
}

function AnyPermissionGate({
  anyOf,
  children,
}: {
  anyOf: readonly Permission[];
  children: ReactNode;
}): JSX.Element {
  const allowed = useCanAny(anyOf);
  // Surface the first permission as the "required" hint on the Forbidden page;
  // the gate itself accepts any of them. anyOf is non-empty by construction
  // (RequireAuth only renders this gate when anyOf.length > 0).
  const hint = anyOf[0] as Permission;
  if (!allowed) return <Forbidden requiredPermission={hint} />;
  return <>{children}</>;
}
